import { tap, catchError, timeout as rxTimeout } from 'rxjs/operators';
import {
	isConfigRequired,
	merge,
} from './utils';
import { logApiEvent } from '../logger';
import { createDevWarn } from '../dev-utils';

const noBodyMethods = ['get', 'head'];
const withBodyMethods = ['post', 'put', 'patch', 'delete'];
const DEFAULT_CONTENT_TYPE = {
	'Content-Type': 'application/json',
};
const defaultConfigs = {
	baseUrl: '',
	method: 'GET',
	headers: {
		Accept: 'application/json',
	},
	fetcher: undefined,
};
const _devWarn = createDevWarn('[api-fetcher]');
// 只挑送 log 真正需要的欄位,避免把整個 AjaxResponse(含 originalEvent / xhr /
// request)塞進 log payload — 單筆可達 8KB+,且 xhr 內含 DOM event,stringify
// 後容易在 Elasticsearch dynamic mapping 對 responseData 衝突。
const _pickAjaxResponse = response => {
	if (!response || typeof response !== 'object') {
		return response;
	}

	return {
		status: response.status,
		response: response.response,
	};
};
const _pickAjaxError = error => {
	if (!error || typeof error !== 'object') {
		return error;
	}

	return {
		name: error.name,
		message: error.message,
		status: error.status,
		response: error.response,
	};
};

function generateApiFetcher(initialConfigs = {}) {
	const _defaults = merge(defaultConfigs, initialConfigs);
	const { fetcher: _fetcher = isConfigRequired('fetcher') } = _defaults;
	const handlers = {};
	const _request = configs => {
		const finalConfigs = merge({}, _defaults, configs);

		finalConfigs.method = finalConfigs.method.toUpperCase();

		const { loggerConfig, timeoutMs } = finalConfigs;

		// 若設定 loggerConfig，請求前先記錄
		if (loggerConfig?.enable) {
			logApiEvent('REQUEST', finalConfigs);
		}

		const stream$ = _fetcher(finalConfigs);
		const withTimeout$ = timeoutMs ? stream$.pipe(rxTimeout(timeoutMs)) : stream$;

		return withTimeout$.pipe(
			tap(response => {
				if (loggerConfig?.enable) {
					try {
						logApiEvent('RESPONSE', finalConfigs, {
							response: _pickAjaxResponse(response),
						});
					} catch (err) {
						// Logger 自身故障不可弄掛 epic;dev 環境提示便於排查
						_devWarn('logApiEvent(RESPONSE) threw', err);
					}
				}
			}),
			catchError(error => {
				if (loggerConfig?.enable) {
					try {
						logApiEvent('ERROR', finalConfigs, {
							error: _pickAjaxError(error),
						});
					} catch (err) {
						_devWarn('logApiEvent(ERROR) threw', err);
					}
				}

				throw error;
			}),
		);
	};

	noBodyMethods.forEach(method => {
		handlers[method] = (url, configs = {}) => {
			return _request(merge(configs, { url, method }));
		};
	});

	withBodyMethods.forEach(method => {
		handlers[method] = (url, body, configs = {}, hasHeader = true) => {
			const baseConfig = hasHeader ? { headers: DEFAULT_CONTENT_TYPE } : {};

			return _request(merge(baseConfig, configs, { url, body, method }));
		};
	});

	return handlers;
}

export default generateApiFetcher;
